import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCreateCategory } from '@/services/mapdata/queries';
import { useAuthStore } from '@/store/auth';
import { showToast } from '@/components/ui/show-toast';
import PageContainer from '@/components/layout/page-container';
import { Heading } from '@/components/ui/heading';
import { BulkUploadModal } from '@/modules/data/bulk-upload-modal';

export default function AddCategoryPage() {
  const navigate = useNavigate();
  const activeWorkspace = useAuthStore(state => state.activeWorkspace);

  const createCategoryMutation = useCreateCategory(
    () => {
      showToast('success', 'Category created successfully');
      navigate('/dashboard/data');
    },
    (error: any) => {
      showToast('error', 'Failed to create category');
      console.error('Create error:', error);
    }
  );

  // Initialize with proper typing
  const [categoryName, setCategoryName] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [newItem, setNewItem] = useState<string>('');

  const toggleItem = (item: string) => {
    setSelectedItems((prev) => 
      prev.includes(item)
        ? prev.filter((i) => i !== item)
        : [...prev, item]
    );
  };

  const handleInputChange = (value: string) => {
    setNewItem(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newItem?.trim()) {
      e.preventDefault();
      const trimmedItem = newItem.trim();
      
      if (!selectedItems.includes(trimmedItem)) {
        setSelectedItems((prev) => [...prev, trimmedItem]);
      }
      setNewItem('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryName.trim()) {
      showToast('error', 'Category name is required');
      return;
    }

    try {
      await createCategoryMutation.mutateAsync({
        workspace: activeWorkspace || '',
        name: categoryName.trim(),
        tags: selectedItems
      });
    } catch (error) {
      // Error is handled in the mutation callbacks
    }
  };

  return (
    <PageContainer scrollable={true}>
      <div className='space-y-6'>
        {/* Header Section */}
        <div className='flex items-center justify-between'>
          <Heading 
            title="Add New Category"
            description="Create a new data category for organizing stakeholder information"
          />
          <div className="flex items-center lg:mr-52">
            <BulkUploadModal />
          </div>
        </div>

        {/* Form Section */}
        <div className='md:max-w-5xl rounded-lg border bg-white p-6 shadow-sm'>
          <div className='mb-6 flex items-center gap-4'>
            <div className='rounded-md bg-primary p-3 text-primary-foreground'>
              <Check className='h-5 w-5' />
            </div>
            <div>
              <h2 className='text-lg font-medium text-primary'>
                New Category
              </h2>
              <p className='text-muted-foreground'>Create and organize data items for your map</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className='space-y-4'>
              {/* Category Name */}
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Category Name *</label>
                <Input
                  placeholder="e.g., Government Agencies, NGOs, Private Companies"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className='bg-background'
                />
              </div>

              {/* Selected Items Display */}
              <div className='rounded-md bg-muted/50 p-4'>
                <h3 className='mb-3 text-sm font-medium'>Data Items</h3>
                <div className='flex flex-wrap gap-2'>
                  {selectedItems.map((item) => (
                    <div
                      key={item}
                      className='flex items-center rounded-full border bg-background px-3 py-1.5 text-sm'
                    >
                      {item}
                      <button
                        type='button'
                        onClick={() => toggleItem(item)}
                        className='ml-2 text-muted-foreground hover:text-destructive'
                      >
                        <X className='h-4 w-4' />
                      </button>
                    </div>
                  ))}
                  {selectedItems.length === 0 && (
                    <p className="text-sm text-muted-foreground">No data items yet</p>
                  )}
                </div>
              </div>

              {/* Add New Item Input */}
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Add Data Item</label>
                <Input
                  placeholder='Type a data item and press Enter'
                  value={newItem}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className='bg-background'
                />
                <p className='text-xs text-muted-foreground'>
                  Press Enter to add the item to the list
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex gap-3 pt-4 justify-between'>
              <Button
                type='button'
                variant='outline'
                onClick={() => navigate('/dashboard/data')}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={createCategoryMutation.isPending}
                className='bg-primary hover:bg-primary/90'
              >
                {createCategoryMutation.isPending ? 'Creating...' : 'Create Category'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </PageContainer>
  );
}